import { getLocalTimeZone, today } from '@internationalized/date'
import {
  addMonths,
  differenceInCalendarMonths,
  format,
  getDate,
  isAfter,
  isBefore,
  lastDayOfMonth,
  parseISO,
  setDate,
  subMonths,
} from 'date-fns'

import type {
  IGerenciadorMensais,
  TCriarProximoGrupoMensais,
  TGrupoMensaisBase,
  TGrupoMensalItem,
} from '@/domain/simulador/gerenciador-mensais.interface'

export class GerenciadorMensaisBase implements IGerenciadorMensais<TGrupoMensalItem> {
  protected obterDataFim(dataFim: string, dataPrevistaEntrega: string): string {
    if (dataFim > dataPrevistaEntrega) {
      return format(subMonths(parseISO(dataFim), 1), 'yyyy-MM-dd')
    }
    return dataFim
  }

  protected clamp(data: Date, diaAlvo: number): Date {
    const ultimoDiaMes = getDate(lastDayOfMonth(data))
    const diaValido = Math.min(diaAlvo, ultimoDiaMes)
    return setDate(data, diaValido)
  }

  protected criar(
    dataInicio: string = today(getLocalTimeZone()).toString(),
    dataFim: string = today(getLocalTimeZone()).toString(),
    valor = 0,
  ): TGrupoMensalItem {
    return {
      dataFim,
      dataInicio,
      quantidade: differenceInCalendarMonths(parseISO(dataFim), parseISO(dataInicio)) + 1,
      valor,
    }
  }

  protected criarComBaseNaEntrega(dataPrevistaEntrega: string, diaInicio: number = 10): TGrupoMensalItem {
    const hoje = parseISO(today(getLocalTimeZone()).toString())
    const dataInicio = setDate(addMonths(hoje, 1), diaInicio)
    const dataFim = this.obterDataFim(
      format(setDate(parseISO(dataPrevistaEntrega), diaInicio), 'yyyy-MM-dd'),
      dataPrevistaEntrega,
    )
    return {
      valor: 0,
      quantidade: differenceInCalendarMonths(parseISO(dataFim), dataInicio) + 1,
      dataInicio: format(dataInicio, 'yyyy-MM-dd'),
      dataFim: format(parseISO(dataFim), 'yyyy-MM-dd'),
    }
  }

  criarGrupoInicial(dataInicio: string, dataFim: string, valor: number): TGrupoMensaisBase {
    if (!dataInicio || !dataFim || !valor) return []
    return [this.criar(dataInicio, dataFim, valor)]
  }

  criarProximoGrupo({ dataPrevisaoEntrega, dataUltimaMensal }: TCriarProximoGrupoMensais, diaInicio = 10) {
    if (!dataPrevisaoEntrega)
      throw new Error('Data prevista de entrega é obrigatória para criar o próximo grupo mensal.')

    if (!dataUltimaMensal) {
      return this.criarComBaseNaEntrega(dataPrevisaoEntrega, diaInicio)
    }

    return this.criarAPartirDeUltimaMensal(dataUltimaMensal, dataPrevisaoEntrega)
  }

  protected criarAPartirDeUltimaMensal(dataUltimaMensal: string, dataPrevisaoEntrega: string): TGrupoMensalItem {
    const novaDataInicio = format(addMonths(parseISO(dataUltimaMensal), 1), 'yyyy-MM-dd')

    const quantidadeMesesRestantes = differenceInCalendarMonths(parseISO(dataPrevisaoEntrega), parseISO(novaDataInicio))

    const novaDataFim = this.obterDataFim(
      format(addMonths(parseISO(novaDataInicio), quantidadeMesesRestantes), 'yyyy-MM-dd'),
      dataPrevisaoEntrega,
    )
    return this.criar(novaDataInicio, novaDataFim)
  }

  calcularAjustePorQuantidade(grupo: TGrupoMensalItem, novaQuantidade: number) {
    if (!grupo.dataInicio) return grupo

    const dataInicioParsed = parseISO(grupo.dataInicio)
    const diaAlvo = getDate(dataInicioParsed)
    const novaDataFim = this.clamp(addMonths(dataInicioParsed, novaQuantidade - 1), diaAlvo)

    if (isBefore(novaDataFim, dataInicioParsed)) {
      return {
        ...grupo,
        dataFim: grupo.dataInicio,
        quantidade: 1,
      }
    }

    return {
      ...grupo,
      dataFim: format(novaDataFim, 'yyyy-MM-dd'),
      quantidade: novaQuantidade,
    }
  }

  calcularAjustePorDataInicio(grupo: TGrupoMensalItem, novaDataInicio: string) {
    if (!grupo.dataFim) return grupo

    const dataFimParsed = parseISO(grupo.dataFim)
    const dataInicioParsed = parseISO(novaDataInicio)
    const diaAlvo = getDate(dataInicioParsed)
    const novaDataFim = this.clamp(dataFimParsed, diaAlvo)

    if (isBefore(novaDataFim, dataInicioParsed)) {
      return {
        valor: grupo.valor,
        dataInicio: novaDataInicio,
        dataFim: novaDataInicio,
        quantidade: 1,
      }
    }

    const novaQuantidade = differenceInCalendarMonths(novaDataFim, dataInicioParsed) + 1

    return {
      valor: grupo.valor,
      dataInicio: novaDataInicio,
      dataFim: format(novaDataFim, 'yyyy-MM-dd'),
      quantidade: Math.abs(novaQuantidade),
    }
  }

  calcularAjustePorDataFim(grupo: TGrupoMensalItem, novaDataFim: string) {
    if (!grupo.dataInicio) return grupo

    const dataInicioParsed = parseISO(grupo.dataInicio)
    const dataFimParsed = parseISO(novaDataFim)
    const diaAlvo = getDate(dataFimParsed)
    const novaDataInicio = this.clamp(dataInicioParsed, diaAlvo)

    if (isAfter(novaDataInicio, dataFimParsed)) {
      return {
        valor: grupo.valor,
        dataInicio: novaDataFim,
        dataFim: novaDataFim,
        quantidade: 1,
      }
    }

    const novaQuantidade = differenceInCalendarMonths(dataFimParsed, novaDataInicio) + 1

    return {
      valor: grupo.valor,
      dataInicio: format(novaDataInicio, 'yyyy-MM-dd'),
      dataFim: format(dataFimParsed, 'yyyy-MM-dd'),
      quantidade: novaQuantidade,
    }
  }
}
